import { useCallback, useEffect, useRef, useState } from 'react';
import { LogOut, LayoutDashboard } from 'lucide-react';
import { useLocation } from 'wouter';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { useAuth } from '@/contexts/AuthContext';
import { goToPortal } from '@/utils/openPortal';
import {
  allowedPortals,
  getPreferredPortalPath,
  hasMultiplePortals,
  portalLabel,
  setPreferredPortal,
} from '@/utils/portalRouting';

function userInitials(name: string | undefined): string {
  if (!name?.trim()) return 'U';
  return name
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface HeaderAuthProps {
  /** Extra classes for the guest Sign in button */
  signInClassName?: string;
  /** Called when the user opens auth or toggles the menu (e.g. close a mobile nav) */
  onInteract?: () => void;
  /** Include a My portal link in the signed-in dropdown (default true) */
  showPortalLink?: boolean;
  /** Visual tone for light homepage nav vs dark app chrome */
  tone?: 'dark' | 'light';
}

export default function HeaderAuth({
  signInClassName,
  onInteract,
  showPortalLink = true,
  tone = 'dark',
}: HeaderAuthProps) {
  const { isAuthenticated, user, logout } = useAuth();
  const [, navigate] = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isLight = tone === 'light';

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (isAuthenticated) setAuthOpen(false);
  }, [isAuthenticated]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    closeMenu();
    await logout();
    setIsSigningOut(false);
  };

  if (!isAuthenticated) {
    return (
      <>
        <button
          type="button"
          className={
            signInClassName ??
            'btn-cta btn-cta-ghost text-sm px-3 py-2 flex min-h-[44px] flex-shrink-0'
          }
          onClick={() => {
            onInteract?.();
            setAuthOpen(true);
          }}
        >
          Sign in
        </button>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      </>
    );
  }

  return (
    <div className="relative flex-shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onInteract?.();
          setMenuOpen((o) => !o);
        }}
        className={`flex items-center gap-2 min-h-[44px] rounded-lg px-1.5 sm:px-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#29ffa9]/50 ${
          isLight ? 'hover:bg-[rgba(17,17,17,0.04)]' : 'hover:bg-white/5'
        }`}
        aria-expanded={menuOpen}
        aria-haspopup="true"
        aria-label={`Signed in as ${user?.email ?? 'user'}`}
      >
        <div
          className={`h-8 w-8 rounded-full bg-[#29ffa9]/20 flex items-center justify-center text-xs font-bold shrink-0 ${
            isLight ? 'text-[#0E9E77]' : 'text-[#29ffa9]'
          }`}
        >
          {userInitials(user?.name)}
        </div>
        <div className="hidden sm:block min-w-0 max-w-[9rem]">
          <p
            className={`text-xs font-medium truncate ${
              isLight ? 'text-[#111111]' : 'text-white'
            }`}
          >
            {user?.name || 'Account'}
          </p>
          <p
            className={`text-[11px] truncate ${
              isLight ? 'text-[#666666]' : 'text-white/40'
            }`}
          >
            {user?.email || ''}
          </p>
        </div>
      </button>

      {menuOpen && (
        <div
          className={`absolute right-0 mt-2 w-56 rounded-lg shadow-xl py-1 z-[60] ${
            isLight
              ? 'border border-[rgba(17,17,17,0.1)] bg-white'
              : 'border border-white/10 bg-[#0d1b2e]'
          }`}
        >
          <div
            className={`px-4 py-3 border-b ${
              isLight ? 'border-[rgba(17,17,17,0.08)]' : 'border-white/8'
            }`}
          >
            <p
              className={`text-sm font-medium truncate ${
                isLight ? 'text-[#111111]' : 'text-white'
              }`}
            >
              {user?.name || 'Account'}
            </p>
            <p
              className={`text-xs truncate ${
                isLight ? 'text-[#666666]' : 'text-white/40'
              }`}
            >
              {user?.email || ''}
            </p>
          </div>
          {showPortalLink && (
            hasMultiplePortals(user) ? (
              allowedPortals(user).map((portal) => (
                <button
                  key={portal}
                  type="button"
                  onClick={() => {
                    setPreferredPortal(portal);
                    closeMenu();
                    goToPortal(portal, navigate);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors text-left ${
                    isLight
                      ? 'text-[#333333] hover:bg-[rgba(17,17,17,0.04)]'
                      : 'text-white/80 hover:bg-white/5'
                  }`}
                >
                  <LayoutDashboard
                    className={`h-4 w-4 shrink-0 ${
                      isLight ? 'text-[#666666]' : 'text-white/40'
                    }`}
                  />
                  {portalLabel(portal)}
                </button>
              ))
            ) : (
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  navigate(getPreferredPortalPath(user));
                }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors text-left ${
                  isLight
                    ? 'text-[#333333] hover:bg-[rgba(17,17,17,0.04)]'
                    : 'text-white/80 hover:bg-white/5'
                }`}
              >
                <LayoutDashboard
                  className={`h-4 w-4 shrink-0 ${
                    isLight ? 'text-[#666666]' : 'text-white/40'
                  }`}
                />
                My portal
              </button>
            )
          )}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={isSigningOut}
            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 transition-colors text-left disabled:opacity-50 ${
              isLight ? 'hover:bg-[rgba(17,17,17,0.04)]' : 'hover:bg-white/5'
            }`}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
